import { Contact } from '@api/generated/graphql';
import { AgentCallerId } from '@components/OutboundCallerIdSelection';
import { AgentCallerIdDetails } from '@components/OutboundCallerIdSelection/AgentCallerId/types';
import { OverflowTooltip } from '@components/OverflowTooltip';
import useTenantName from '@lib/hooks/useTenantName';
import { getTestIdProps } from '@lib/testUtils';
import { inboxLocators } from '@locators/inbox';
import { Flex, Text } from '@servicetitan/anvil2';
import { Button, ButtonGroup, Modal } from '@servicetitan/design-system';
import { ModalSizes } from '@servicetitan/design-system/dist/components/Modal/components';
import { useCallback, useState } from 'react';
import { OutboundCallerIdConfirmationHeader } from './OutboundCallerIdConfirmationHeader';
import { UNKNOWN_TENANT } from './constants';
import strings from './strings';
import classNames from 'classnames';
import styles from './AgentCallerIdConfirmationModal.module.css';

interface AgentCallerIdConfirmationModalProps {
  customerName?: string;
  contact: Contact;
  onClose: () => void;
  tenantId: string;
  onCall?: (data: any) => Promise<void> | void;
  callButtonId?: string;
}

export const AgentCallerIdConfirmationModal = ({
  customerName,
  contact,
  tenantId,
  onClose,
  onCall,
  callButtonId,
}: AgentCallerIdConfirmationModalProps) => {
  const accountName = useTenantName(tenantId);
  const displayName = accountName.length ? accountName : UNKNOWN_TENANT;
  const [fromData, setFromData] = useState<AgentCallerIdDetails>({
    callerId: '',
    callerName: '',
  });
  const [loading, setLoading] = useState(false);

  const handleOnCall = useCallback(async () => {
    setLoading(true);
    try {
      await onCall?.(fromData.callerId);
    } catch {
      setLoading(false);
    }
  }, [onCall, fromData]);

  return (
    <Modal
      open={true}
      focusTrapOptions={{ disabled: true }}
      size={ModalSizes.S}
      title={
        <OutboundCallerIdConfirmationHeader
          customerName={customerName}
          customerNumber={contact?.value}
          memo={contact?.memo ?? undefined}
        />
      }
      className={styles.agentCallerIdModal}
      footer={
        <ButtonGroup>
          <Button onClick={onClose}>{strings.labels.secondaryText}</Button>
          <Button
            primary
            onClick={handleOnCall}
            loading={loading}
            disabled={loading}
            {...getTestIdProps(callButtonId)}
          >
            {strings.labels.primaryText}
          </Button>
        </ButtonGroup>
      }
    >
      <Flex direction='column' justifyContent={''} alignItems={''}>
        <Flex justifyContent={'space-between'} gap='8'>
          <div
            className={classNames(
              styles.tenantSection,
              styles.callingFromSection,
            )}
          >
            <Text
              variant='body'
              size='medium'
              className={styles.tenantFromTitle}
            >
              {strings.labels.fromTitle}
            </Text>
            <OverflowTooltip
              className={styles.tenantNameText}
              content={displayName}
            >
              <Text
                variant='body'
                size='medium'
                className={styles.tenantNameText}
                {...getTestIdProps(
                  inboxLocators.customerSearch
                    .outboundConfirmationModalSelectedTenant,
                )}
              >
                {displayName}
              </Text>
            </OverflowTooltip>
          </div>

          <div className={styles.tenantSection}>
            <Text
              variant='body'
              size='medium'
              className={styles.tenantFromTitle}
            >
              {strings.labels.callerIdTitle}
            </Text>
            <AgentCallerId
              tenantId={tenantId}
              tenantName={accountName}
              onChange={setFromData}
              onLoadingChange={setLoading}
            />
          </div>
        </Flex>
      </Flex>
    </Modal>
  );
};
